import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { enumEntrySummarySubmissionType, enumEntrySummaryType, enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { DocumentCreateModel, DocumentModel } from '@data-layer/models/documents';
import { enumDocumentType } from '@data-layer/enums/documents';
import { ShortModel, ValidationMessageModel } from '@data-layer/models/common';
import { EntrySummaryActionsHelper } from '@helpers/entry-summary-actions.helper';
import { EntrySummaryStatusHelper } from '@helpers/entry-summary-status.helper';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { DeliveryOrderModel, EntrySummaryDutiesModel, EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { PaymentBreakdownHelper } from '@helpers/payment-breakdown.helper';
import { BillItemPendingModel, PaymentBillItemPendingModel } from '@data-layer/models/payments';
import { GenericService } from '@services/generic.service';
import { Observable } from 'rxjs';
import { ManagementEntrySummaryModel } from '@data-layer/models/management';
import { IEsDocumentStateUpdate } from '@shared/es-mutual/interfaces';
import { DateHelper } from '@helpers/date.helper';
import { IPaymentBreakdownItemGroup } from '@interfaces/payment-breakdown-group';
import { MarineInsuranceSideformService } from '@shared/marine-insurance-shared/services';
import { enumMarineInsuranceEntityType, enumMarineInsurancePendingPaymentAction } from '@data-layer/enums/marine-insurance';
import { MarineInsuranceModel } from '@data-layer/models/marine-insurance';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { enumPaymentState } from '@app/data-layer/enums/payments';

@Component({
  selector: 'es-details',
  templateUrl: './es-details.component.html',
  styleUrls: ['./es-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsDetailsComponent implements OnInit, OnChanges {
  @Input() public deliveryOrdersInfo: DeliveryOrderModel[] = [];
  @Input() public entrySummary: EntrySummaryModel | ManagementEntrySummaryModel;
  @Input() public attachedDocuments: DocumentModel[] = [];
  @Input() public isCanEdit: boolean = false;
  @Input() public isCanEditBond: boolean = false;
  @Input() public isShowDocuments: boolean;
  @Input() public isCanUploadDocuments: boolean = false;
  @Input() public isCanRemoveDocuments: boolean = true;
  @Input() public isOverrideDefaultPoaRequired: boolean = false;
  @Input() public canUploadPunToCompany: boolean = true;
  @Input() public dutiesValue: EntrySummaryDutiesModel;
  @Input() public allValidationMessages: ValidationMessageModel[] = [];
  @Input() public isOpenInvoicesOnEdit: boolean = false;
  @Input() public companyPaymentType: enumCompanyPaymentType = enumCompanyPaymentType.INSTANT;
  @Input() public isShowActions: boolean = false;
  @Input() public pendingBills: BillItemPendingModel[] = [];
  @Input() public canceledBills: BillItemPendingModel[] = [];
  @Input() public onlyLastVersionForFormDocuments: boolean = true;
  @Input() public connectedIsfs: ShortModel[] = [];
  @Input() public enumEntrySummarySubmissionType = enumEntrySummarySubmissionType;
  @Input() public isHideDownloadAll: boolean;
  @Input() public isInsuranceAvailable: boolean = false;
  @Input() public isProviderWasDown: boolean = false;
  @Input() public isInsuranceLoading: boolean;
  @Input() public isManagement: boolean;
  @Input() public currentInsurance: MarineInsuranceModel;
  @Input() public currentInsuranceDocuments: DocumentModel[];
  @Input() public currentInsurancePendingBills: PaymentBillItemPendingModel;

  @Input() public uploadCompanyFile: (document: DocumentCreateModel, file: File, uploadToCompany: boolean) => Observable<any>;
  @Input() public uploadFile: (document: DocumentCreateModel, file: File) => Observable<any>;
  @Input() public removeFile: (documentId: number) => Observable<any>;

  @Output() public editShipmentData: EventEmitter<any> = new EventEmitter();
  @Output() public editImporter: EventEmitter<any> = new EventEmitter();
  @Output() public editBond: EventEmitter<any> = new EventEmitter();
  @Output() public editShipmentBuyer: EventEmitter<number> = new EventEmitter();
  @Output() public editShipment: EventEmitter<number> = new EventEmitter();
  @Output() public editShipmentConsignee: EventEmitter<number> = new EventEmitter();
  @Output() public editSupplier: EventEmitter<number> = new EventEmitter();
  @Output() public editInvoice: EventEmitter<{ shipmentId: number; invoiceId: number }> = new EventEmitter();
  @Output() public removeInvoice: EventEmitter<{ shipmentId: number; invoiceId: number }> = new EventEmitter();
  @Output() public editInvoices: EventEmitter<number> = new EventEmitter();
  @Output() public removeShipment: EventEmitter<number> = new EventEmitter();
  @Output() public documentStateUpdate: EventEmitter<IEsDocumentStateUpdate> = new EventEmitter();
  @Output() public poaSidebarRequest: EventEmitter<enumDocumentType> = new EventEmitter<enumDocumentType>();
  @Output() public applyCoupon = new EventEmitter<string>();

  public DEFAULT_CURRENCY_CODE = DEFAULT_CURRENCY_CODE;
  public isShowPaymentCard: boolean = false;
  public paymentBreakdown: IPaymentBreakdownItemGroup[] = [];
  public isPrinting$: Observable<boolean>;
  public isLateEntry = false;

  public enumUserBetaFlag: typeof enumUserBetaFlag = enumUserBetaFlag;
  public enumMarineInsuranceEntityType: typeof enumMarineInsuranceEntityType = enumMarineInsuranceEntityType;
  public enumPaymentState: typeof enumPaymentState = enumPaymentState;
  public paidOrSentPaymentStates: enumPaymentState[] = [
    enumPaymentState.SUCCESS,
    enumPaymentState.PARTIALLY_REFUNDED,
    enumPaymentState.DELAYED,
  ];
  public enumMarineInsurancePendingPaymentAction: typeof enumMarineInsurancePendingPaymentAction = enumMarineInsurancePendingPaymentAction;

  public get isShowBond(): boolean {
    return this.entrySummary.type !== enumEntrySummaryType.INFORMAL;
  }

  constructor(private genericService: GenericService, private marineInsuranceSideformService: MarineInsuranceSideformService) {
    this.isPrinting$ = this.genericService.isPrinting$;
  }

  public ngOnInit(): void {
    this.isLateEntry =
      this.entrySummary.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED &&
      this.entrySummary.arrivalDate < DateHelper.addDaysByUTC(DateHelper.startOfTheDayByUTC(DateHelper.now), -7);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.pendingBills) {
      this.buildPaymentCard();
    }
  }

  public get canRemoveDocuments(): boolean {
    return this.isCanRemoveDocuments && EntrySummaryActionsHelper.isCanRemoveDocuments(this.entrySummary);
  }

  public get isHasInstantPayment(): boolean {
    return (this.pendingBills || []).some((bill) => bill.paymentType === enumCompanyPaymentType.INSTANT);
  }

  public onDocumentStateUpdate(event: IEsDocumentStateUpdate) {
    this.documentStateUpdate.emit(event);
  }

  public onPoaSidebarRequest(docType: enumDocumentType): void {
    this.poaSidebarRequest.emit(docType);
  }

  public onEditShipmentData() {
    this.editShipmentData.emit();
  }

  public onEditImporter() {
    this.editImporter.emit();
  }

  public onEditShipmentBuyer(shipmentId: number) {
    this.editShipmentBuyer.emit(shipmentId);
  }

  public onEditBond() {
    this.editBond.emit();
  }

  public onEditShipment(data?: number) {
    this.editShipment.emit(data);
  }

  public onRemoveShipment(id: number) {
    this.removeShipment.emit(id);
  }

  public onEditShipmentConsignee(data: number) {
    this.editShipmentConsignee.emit(data);
  }

  public onEditSupplier(data: number) {
    this.editSupplier.emit(data);
  }

  public onEditInvoice(data: { shipmentId: number; invoiceId: number }) {
    this.editInvoice.emit(data);
  }

  public onRemoveInvoice(data: { shipmentId: number; invoiceId: number }) {
    this.removeInvoice.emit(data);
  }

  public onEditInvoices(data: number) {
    this.editInvoices.emit(data);
  }

  public openMarineInsuranceQuoteForm(isEdit?: boolean): void {
    this.marineInsuranceSideformService.onOpenQuoteSidebar(
      enumMarineInsuranceEntityType.ENTRY_SUMMARY,
      {
        entrySummary: this.entrySummary as EntrySummaryModel,
      },
      this.currentInsurance,
      isEdit,
    );
  }

  public get isEsSent(): boolean {
    return EntrySummaryStatusHelper.isSent(this.entrySummary?.state);
  }

  /** #13499 — the mobile BL cards show "Bill of Lading #N" only when the ES has more than one BL. */
  public get isMultiBl(): boolean {
    return !!this.entrySummary?.shipments && this.entrySummary.shipments.length > 1;
  }

  public get isTruck(): boolean {
    return this.entrySummary?.transportationMode === enumTransportationMode.TRUCK;
  }

  private buildPaymentCard() {
    this.paymentBreakdown = PaymentBreakdownHelper.buildPaymentBreakdownFromPendingBills(
      this.entrySummary,
      this.pendingBills,
      this.canceledBills,
    );
    this.isShowPaymentCard = this.paymentBreakdown.length > 0;
  }
}
